<script setup lang="ts">
import { computed } from 'vue'
import { VCard, VCardTitle, VSection, VStats } from '@/modules/shared/components'
import ValueOverTimeChart from '@/modules/investing/components/overview/value-over-time-chart.vue'
import { initialMoney, toNumber, type Money } from '@/modules/shared/utils/money'
import type { MetricSeriesPoint } from '@/modules/investing/stores/better-entity-store'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'
import { useExtendedI18n } from '@/i18n'
import { useRoute } from 'vue-router'

type InsightsShape = {
  initial_value?: Money | null
  current_value?: Money | null
  portfolio_distributed?: Money | null
  tvpi?: number | null
  dpi?: number | null
  rvpi?: number | null
  moic?: number | null
}

const props = defineProps<{
  skeleton?: boolean
  isAdmin?: boolean
  insights?: InsightsShape | null
  companies?: any[]
  industries?: any[]
  metricSeriesPoints?: MetricSeriesPoint[]
  metricSeriesCurrency?: string
  metricSeriesLoading?: boolean
}>()

const { hidden } = useHiddenMetrics()
const route = useRoute()
const { n } = useExtendedI18n()

const isPortfolioValueOverTimeEnabled = computed(() => {
  const envVar = import.meta.env.VITE_FF_INVESTING_PORTFOLIO_VALUE_OVER_TIME_ALLOWLIST
  if (!envVar || envVar.trim() === '') return false
  if (envVar === 'all') return true
  const slug = route.params.slug as string
  if (!slug) return false
  return envVar
    .split(',')
    .map((s) => s.trim())
    .includes(slug)
})

const insights = computed<InsightsShape>(() => props.insights ?? {})
const initialValue = computed<Money>(() => insights.value.initial_value ?? initialMoney)
const formattedInitialValue = computed(() => n(initialValue.value, 'currency', undefined, true, 0))
const currentValue = computed<Money>(() => insights.value.current_value ?? initialMoney)
const formattedCurrentValue = computed(() => n(currentValue.value, 'currency', undefined, true, 0))
const companiesFunded = computed(() => props.companies?.length ?? 0)

const distributed = computed<Money>(() => insights.value.portfolio_distributed ?? initialMoney)

const showValueChart = computed(
  () => toNumber(currentValue.value) + toNumber(distributed.value) > 0 || !!props.metricSeriesPoints?.length,
)

const displaySeriesPoints = computed<MetricSeriesPoint[]>(() => {
  const points = props.metricSeriesPoints ?? []

  const totalValue = toNumber(currentValue.value) + toNumber(distributed.value)
  if (!points.length && totalValue <= 0) return points

  const today = new Date().toISOString().slice(0, 10)
  if (points.length && points[points.length - 1].date >= today) return points

  return [...points, { date: today, value_amount: totalValue }]
})
</script>

<template>
  <VCard>
    <VCardTitle title="Portfolio Overview" />
    <div
      class="mb-6 grid divide-x divide-gray-700 rounded-lg bg-[#1E3448] [box-shadow:var(--shadow-ring),var(--shadow-elevation)]"
      :class="hidden.initialValue ? 'grid-cols-2' : 'grid-cols-3'"
    >
      <div v-if="!hidden.initialValue" class="px-4 py-3 text-center">
        <div class="mb-1">
          <div class="flex items-center justify-center text-xs font-semibold text-gray-300">
            <span class="whitespace-nowrap">Initial Value</span>
          </div>
        </div>
        <h3 class="text-lg font-medium tracking-tight text-white sm:text-xl">
          <span v-if="skeleton">—</span>
          <span v-else>{{ formattedInitialValue }}</span>
        </h3>
      </div>
      <div class="px-4 py-3 text-center">
        <div class="mb-1">
          <div class="flex items-center justify-center text-xs font-semibold text-gray-300">
            <span class="whitespace-nowrap">Current Value</span>
          </div>
        </div>
        <h3 class="text-lg font-medium tracking-tight text-white sm:text-xl">
          <span v-if="skeleton">—</span>
          <span v-else class="text-[#7abfb8]">{{ formattedCurrentValue }}</span>
        </h3>
      </div>
      <div class="px-4 py-3 text-center">
        <div class="mb-1">
          <div class="flex items-center justify-center text-xs font-semibold text-gray-300">
            <span class="whitespace-nowrap">Companies Funded</span>
          </div>
        </div>
        <h3 class="text-lg font-medium tracking-tight text-white sm:text-xl">
          <span v-if="skeleton">—</span>
          <span v-else>{{ companiesFunded }}</span>
        </h3>
      </div>
    </div>
    <ValueOverTimeChart
      v-if="showValueChart && isPortfolioValueOverTimeEnabled"
      class="mb-6"
      :points="displaySeriesPoints"
      :currency="metricSeriesCurrency"
      :loading="metricSeriesLoading"
    />

    <VSection class="!mb-1 mt-6" label="Key Performance Metrics">
      <VStats
        :skeleton="skeleton"
        :stats="[
          { colspan: 1, label: 'TVPI', type: 'multiple', value: insights.tvpi ?? 0 },
          { colspan: 1, label: 'DPI', type: 'multiple', value: insights.dpi ?? 0 },
          { colspan: 1, label: 'RVPI', type: 'multiple', value: insights.rvpi ?? 0 },
          { colspan: 1, label: 'MOIC', type: 'multiple', value: insights.moic ?? 0, visible: !hidden.moic },
        ]"
      />
    </VSection>
  </VCard>
</template>
